import { Document } from '@contentful/rich-text-types'
import { css } from '@emotion/core'
import { graphql } from 'gatsby'
import { renderRichText } from 'gatsby-source-contentful/rich-text'
import React from 'react'
import { Helmet } from 'react-helmet'

import Anchors from '../components/Anchors'
import Block from '../components/Block'
import Body from '../components/Body'
import { CardsNavigation } from '../components/CardsNavigation'
import { categoryLinkStyle } from '../components/content-management/blog/BlogPostCard'
import RichText from '../components/content-management/RichText'
import CTAEbook from '../components/ctas/CTAEbook'
import CTAMain from '../components/ctas/CTAMain'
import Divider from '../components/Divider'
import { FAQ, FAQContainer, FAQdata } from '../components/FAQ'
import Footer from '../components/Footer'
import Header from '../components/Header'
import { IntroHeroBlock } from '../components/IntroHeroBlock'
import Layout from '../components/Layout'
import { Link } from '../components/Link'
import ReadingProgressBar from '../components/ReadingProgressBar'
import { Schema } from '../components/Schema'
import { SearchResultsBlock } from '../components/Search'
import SEO from '../components/SEO'
import SocialSharing from '../components/SocialSharing'
import TextCard from '../components/TextCard'
import { glossaryBreakPoint, headerWithSearchBreakPoint } from '../constants/breakPoints'
import { getAnchors, richTextRenderingOptions } from '../helpers/contentful'
import { getCtaEbookUrl, getCtaMainType } from '../helpers/cta'
import { getSearchHeaderEntriesLogin } from '../helpers/header'
import { useDebouncedValue } from '../hooks/useDebouncedValue'
import { GlossaryTermPageProps } from '../interfaces/glossary'
import { HeaderProps } from '../interfaces/header'
import { glossaryTextCardStyle } from '../pages/glossary'
import { anchorsFallbackStyle } from './blog/BlogPost'
import { longCopyBlockStyle } from './resources/GuidePage'

export const GlossaryHeader: React.FunctionComponent<HeaderProps> = (props) => {
  return (
    <Header
      entriesLogin={getSearchHeaderEntriesLogin}
      customBreakPoint={headerWithSearchBreakPoint}
      searchPlaceholder={'Search Glossary...'}
      {...props}
    />
  )
}

export const GlossaryTermPageTemplate: React.FunctionComponent<GlossaryTermPageProps> = ({ data }) => {
  const [search, setSearch] = React.useState('')
  const debouncedSearch = useDebouncedValue(search)

  // filter searchTerms by (debounced) search term
  const filteredSearchTerms = React.useMemo(
    () =>
      data.allGlossaryTerms.edges.filter(({ node: { title } }) =>
        title.toLowerCase().includes(debouncedSearch.toLowerCase())
      ),
    [debouncedSearch]
  )

  const { glossaryTerm } = data
  const json = React.useMemo(() => JSON.parse(glossaryTerm.content.raw) as Document, [glossaryTerm])
  const anchors = React.useMemo(() => getAnchors(json), [glossaryTerm])

  const categorySlug = data.glossaryTerm.category[0].slug

  const ctaEbookUrl = getCtaEbookUrl(categorySlug)

  // enhancing overwriting contentObject with cta type and eBook slug for roadmapping
  const glossaryContent = {
    raw: glossaryTerm.content.raw,
    references: glossaryTerm.content.references.map((reference) =>
      reference.__typename === 'ContentfulCrossSellingEntry'
        ? {
            ...reference,
            type: 'eBook / Report',
            ebookResource: {
              slug: ctaEbookUrl,
            },
          }
        : { ...reference }
    ),
  }

  return (
    <Layout>
      <SEO
        title={glossaryTerm.seoTitle}
        withTitleTemplate={false}
        description={glossaryTerm.seoAbstract}
        imageSrc={glossaryTerm.seoImage ? `https:${glossaryTerm.seoImage.file.url}` : undefined}
      />
      <Schema
        type={'BlogPosting'}
        url={glossaryTerm.slug}
        headline={glossaryTerm.title}
        abstract={glossaryTerm.abstract.abstract}
        author={'airfocus GmbH'}
        dateCreated={glossaryTerm.createdAt}
      />

      {!!glossaryTerm.frequentlyAskedQuestions && glossaryTerm.frequentlyAskedQuestions.length > 0 && (
        <Helmet>
          <script type={'application/ld+json'}>
            {`
              {
                "@context": "https://schema.org",
                "@type": "FAQPage",
                "mainEntity": [${FAQdata(glossaryTerm.frequentlyAskedQuestions)?.join(',') ?? ''}]}]
              }
            `}
          </script>
        </Helmet>
      )}

      <GlossaryHeader hideOnScroll search={search} setSearch={setSearch} />
      <Body>
        <ReadingProgressBar />
        <IntroHeroBlock
          childrenIsFirst
          children={
            <Link
              className={'white-text opacity-on-hover'}
              href={`/glossary/category/${glossaryTerm.category[0].slug}/`}
            >
              {glossaryTerm.category[0].name}
            </Link>
          }
          headlineText={glossaryTerm.title}
          blueBG
          withoutCTA
          withoutMarginBottom
          cssStyles={glossaryTermIntroStyle}
        />
        <Block css={longCopyBlockStyle}>
          {anchors.length === 0 ? (
            <h3 className={'heading3 no-margin hide-on-small-medium'} css={anchorsFallbackStyle}>
              {glossaryTerm.title}
            </h3>
          ) : (
            <Anchors anchors={anchors} cssProp={anchorsStyle} />
          )}

          <div>
            <RichText>{renderRichText(glossaryContent, richTextRenderingOptions)}</RichText>
            {!!glossaryTerm.frequentlyAskedQuestions && glossaryTerm.frequentlyAskedQuestions.length > 0 && (
              <div
                css={css`
                  margin: 4em 0 0em 0;
                `}
              >
                <h2
                  css={css`
                    margin: 0em 0 1em 0;
                  `}
                >
                  General FAQ
                </h2>
                <div className={'rich-text'} css={[FAQContainer, FAQStyle]}>
                  {glossaryTerm.frequentlyAskedQuestions.map(
                    ({ question: { question }, answer: { answer } }, index) => (
                      <FAQ key={index} question={question} answer={answer} />
                    )
                  )}
                </div>
              </div>
            )}
            <Divider size={4} />
            <SocialSharing title={glossaryTerm.title} />
          </div>
          <CTAEbook isSidebarCta educationResourceUrl={ctaEbookUrl} cssStyles={ctaSidebarStyle} />
        </Block>

        <Divider size={8} />

        <CardsNavigation
          categories={data.glossaryCategories.edges}
          heading={'Glossary categories'}
          hrefPrefix={'glossary/category'}
        />

        <CTAMain type={getCtaMainType(categorySlug)} />

        {/* slide-up for search results (overlay) */}
        <SearchResultsBlock
          search={search}
          debouncedSearch={debouncedSearch}
          resultsArray={filteredSearchTerms}
          resultsBlockContent={filteredSearchTerms.map(({ node: filteredTerm }) => (
            <TextCard
              additionalJsx={
                <Link
                  className={'strong-body-2 opacity-on-hover'}
                  href={`/glossary/category/${filteredTerm.category[0].slug}/`}
                  css={categoryLinkStyle(filteredTerm.category[0].categoryColor)}
                >
                  {filteredTerm.category[0].name}
                </Link>
              }
              key={filteredTerm.slug}
              title={filteredTerm.title}
              description={filteredTerm.seoAbstract}
              linkText={'Read more'}
              linkUrl={`/glossary/${filteredTerm.slug}/`}
              cssStyles={glossaryTextCardStyle}
            />
          ))}
          breakPoint={glossaryBreakPoint}
        />
      </Body>
      <Footer />
    </Layout>
  )
}

export default GlossaryTermPageTemplate

export const query = graphql`
  query GlossaryTermPage($id: String!) {
    glossaryTerm: contentfulGlossaryTerm(id: { eq: $id }) {
      id
      slug
      title
      category {
        name
        slug
      }
      createdAt
      abstract {
        abstract
      }
      seoTitle
      seoAbstract
      seoImage {
        file {
          url
        }
      }
      content {
        raw
        references {
          ... on ContentfulAsset {
            # contentful_id and __typename are required to resolve the references
            title
            contentful_id
            __typename
            gatsbyImageData(width: 800, quality: 90)
          }
          ... on ContentfulDefinitionCard {
            id
            contentful_id
            __typename
            title
            description {
              description
            }
          }
          ... on ContentfulCrossSellingEntry {
            id
            type
            ebookResource {
              slug
            }
            contentful_id
            __typename
            title
          }
          ... on Node {
            ... on ContentfulImageWithLink {
              id
              contentful_id
              __typename
              link
              image {
                title
                gatsbyImageData(width: 800, quality: 90)
              }
              description {
                description
              }
            }
          }
        }
      }
      category {
        slug
        name
      }
      frequentlyAskedQuestions {
        question {
          question
        }
        answer {
          answer
        }
      }
    }

    allGlossaryTerms: allContentfulGlossaryTerm(sort: { title: ASC }) {
      edges {
        node {
          id
          slug
          title
          abstract {
            abstract
          }
          seoAbstract
          category {
            slug
            name
            categoryColor
          }
        }
      }
    }

    glossaryCategories: allContentfulGlossaryTermCategory(sort: { name: ASC }) {
      edges {
        node {
          id
          slug
          name
          navigationIcon {
            gatsbyImageData(width: 60, quality: 90)
          }
        }
      }
    }
  }
`

const glossaryTermIntroStyle = css`
  padding-top: 3rem;

  .heading1 {
    pointer-events: none !important;
  }
`

const anchorsStyle = css`
  margin-top: 0.5rem;
`

const ctaSidebarStyle = css`
  margin-top: 0.75rem;
`

const FAQStyle = css`
  width: 100%;
`
